// ─────────────────────────────────────────────────────────────────────────────
// ShippingExclusionModalComponent
// ─────────────────────────────────────────────────────────────────────────────
// WB-7570: dialog to add one or more locations to the shipping-exclusion
// Knowledge Base list. Two steps:
//   1. search/filter -- free-text key search (name/company/city/zip/ERP code,
//      server-side) + a Filter dialog with static location criteria (has a
//      billing address) AND one dynamic field per `customer`-object-type
//      custom field definition (text/boolean/single-select/multi-select),
//      results as a checkbox multi-select list, paginated PAGE_SIZE at a time
//      and extended by scrolling the results body. Every fetch opts into
//      `exclude_shipping_excluded`: a location already on the list is a
//      no-op if re-added, so it's dropped from the search/listing entirely.
//
//      Selecting more than what's on screen doesn't require paging it all
//      in first: "Select all N matching" fetches just the ids (+ display
//      name/erp_id) for every match from a dedicated, unpaginated endpoint
//      and adds them to the selection directly -- browsing stays a small,
//      fast, scroll-paginated list regardless of how many locations actually
//      match.
//   2. preview -- read-only recap of the selected locations before confirming.
// Confirm dispatches one BulkCreateLocationShippingExclusions -- the effect
// sends the whole batch as ONE request; a duplicate/already-excluded/invalid
// id is counted (not raised) by the backend, so a mixed batch still adds
// everything it can.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, take, takeUntil } from 'rxjs/operators';

import { AppState } from 'src/app/core/app.state';
import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';
import { SharedDialogComponent } from 'src/app/ui/shared-dialog/shared-dialog.component';
import { LanguageService } from 'src/app/config/language.service';
import { CustomersService } from 'src/app/features/customers/services/customers.services';
import { ICompanyFilterRequest, ILocation, ILocationSelectAllRow } from 'src/app/features/customers/models/customers.models';
import { SettingsService } from 'src/app/features/settings/services/settings.services';
import { ICustomField } from 'src/app/features/settings/models/settings.models';
import { CustomFieldType, CustomFieldTypes, RelatedObjectsTypes } from 'src/app/features/settings/models/custom-fields.constants';
import { CompaniesGroupsService } from 'src/app/features/companies-groups/services/companies-groups.service';
import { ICompaniesGroups } from 'src/app/features/companies-groups/models/companies-groups.model';
import { FilterDialogComponent } from 'src/app/shared/components/filter-dialog/filter-dialog.component';
import { FilterDialogData, FilterField, FilterFieldType } from 'src/app/shared/components/filter-dialog/filter-dialog.models';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';

import { BulkCreateLocationShippingExclusions, BulkCreateLocationShippingExclusionsSuccess } from '../../store/location-shipping-exclusions.actions';
import { selectLocationShippingExclusionCreating } from '../../store/location-shipping-exclusions.selectors';

type Step = 'search' | 'preview';

/** Page size for every browsing fetch -- the bare (no criteria) list, a
 * keyword search and an active filter all page the same way. Scrolling the
 * results body requests further pages (see `loadMoreLocations`); bulk
 * selection beyond what's paged in is handled separately (see
 * `selectAllMatching`), so this never needs to grow to "fetch everything". */
const PAGE_SIZE = 50;

/** Scrolled far enough down the results body to fetch the next page. */
const SCROLL_LOAD_MORE_THRESHOLD = 0.8;

/** Cap on rows rendered in the preview-step list. A "select all N matching"
 * selection can hold tens of thousands of ids; rendering (and rebuilding, on
 * every change-detection tick) an array that large would make the preview
 * step visibly janky for no benefit -- the admin is confirming a count and
 * spot-checking a few names, not reviewing every row. */
const PREVIEW_ROWS_LIMIT = 200;

/** Synthetic FilterDialogComponent key for a dynamic customer custom field. */
const customFieldKey = (definitionId: number): string => `cf_${definitionId}`;

/** Custom field types this filter UI knows how to render + translate into a
 * backend `custom_field` filter clause. rich_text/number/decimal/date are
 * intentionally not offered yet -- they need a richer FilterDialogComponent
 * "range"/"date-range" round-trip than a location picker filter warrants. */
const FILTERABLE_CUSTOM_FIELD_TYPES = new Set<CustomFieldType>([
  CustomFieldTypes.TEXT,
  CustomFieldTypes.URL,
  CustomFieldTypes.EMAIL,
  CustomFieldTypes.BOOLEAN,
  CustomFieldTypes.SINGLE_SELECT,
  CustomFieldTypes.MULTI_SELECT,
]);

@Component({
  selector: 'app-shipping-exclusion-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, SharedModule, UiModule, SharedDialogComponent],
  templateUrl: './shipping-exclusion-modal.component.html',
  styleUrls: ['./shipping-exclusion-modal.component.scss'],
})
export class ShippingExclusionModalComponent implements OnInit, OnDestroy {
  // ── Step ──────────────────────────────────────────────────────────────────

  step: Step = 'search';

  // ── Search state ──────────────────────────────────────────────────────────

  searchControl = new FormControl('');
  keyword = '';
  activeFilterValues: Record<string, any> = {};

  sortedLocations: (ILocation & { id: number })[] = [];
  loading = false;

  /** Page currently loaded into `sortedLocations` (1-based). */
  private pageNumber = 1;
  /** True while a scroll-triggered next-page fetch is in flight. */
  isLoadingMore = false;
  /** False once a fetch returns fewer than `PAGE_SIZE` rows -- nothing left to page in. */
  canLoadMore = false;
  /** The server's true total match count for the current search/filter (from
   * `metadata.count`), independent of how many rows are actually paged into
   * `sortedLocations`. Drives the "Select all N matching" label; `null`
   * until the first page resolves. */
  matchingCount: number | null = null;

  /**
   * Selection state, deliberately NOT the shared `SelectionController`
   * (used by ~30 other list surfaces): that utility prunes selection down to
   * whatever's currently rendered on the theory that a bulk action must
   * never reach a row the user can't see -- exactly wrong for "select all N
   * matching", which has to hold ids that were never paged into
   * `sortedLocations`. Keyed by id so a row picked via select-all and later
   * scrolled into view (or vice versa) is recognized as the same row.
   */
  private selectedById = new Map<number, ILocationSelectAllRow>();
  /** True once "Select all N matching" populated the selection from the
   * dedicated endpoint; cleared by any manual deselect so the label/preview
   * never claims a completeness that's no longer true. */
  selectAllMatchingActive = false;
  /** True while the "select all N matching" request is in flight. */
  isSelectingAll = false;

  // ── Filter definitions ────────────────────────────────────────────────────

  customFieldDefinitions: ICustomField[] = [];
  /** Customer groups (`wl.locations_groups` -- same model backing the
   * "Company groups" nav section), offered as a multi-select LOCATION
   * CRITERIA filter alongside has_billing/is_main. */
  locationGroups: ICompaniesGroups[] = [];
  /** True once BOTH the customer custom-field definitions fetch and the
   * customer groups fetch have settled (success or error, each independently
   * -- see ngOnInit). FilterDialogComponent snapshots `filterConfig` once at
   * open time and never re-reads it -- opening the Filter dialog before this
   * resolves would silently omit the "Customer Fields" section and/or the
   * groups option list, and reopening later wouldn't fix an already-open
   * dialog. Gating the Filter button on this closes that race. */
  filterOptionsReady = false;
  private readonly currentLanguage = this.languageService.getCurrentLanguage() || 'en';

  // ── Permission / submit state ────────────────────────────────────────────

  canSubmit = false;
  canSubmit$: Observable<boolean> = of(false);
  submitting$: Observable<boolean> = of(false);

  // ── Cleanup ───────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();
  /** Emits whenever a new search/filter starts, to cancel a still-in-flight
   * page fetch from the PREVIOUS one (a fresh `loadLocations()` call resets
   * `pageNumber`/`sortedLocations`; a stale scroll-page-2 response for the
   * old keyword landing afterwards would splice into the new list and
   * overwrite `pageNumber`, silently skipping a page of the new search). */
  private cancelPendingFetch$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<ShippingExclusionModalComponent>,
    private dialog: MatDialog,
    private store: Store<AppState>,
    private actions$: Actions,
    private translateService: TranslateService,
    private languageService: LanguageService,
    private customersService: CustomersService,
    private settingsService: SettingsService,
    private companiesGroupsService: CompaniesGroupsService,
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.canSubmit$ = this.store.select(selectCanDo('company', 'edit'));
    this.canSubmit$.pipe(takeUntil(this.destroy$)).subscribe((canSubmit) => {
      this.canSubmit = canSubmit;
    });

    this.submitting$ = this.store.select(selectLocationShippingExclusionCreating);

    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => this.onSearch(value ?? ''));

    this.actions$.pipe(ofType(BulkCreateLocationShippingExclusionsSuccess), take(1), takeUntil(this.destroy$)).subscribe(() => this.close());

    forkJoin({
      customFieldDefinitions: this.settingsService
        .listCustomFields({ number: 1, size: 100 }, RelatedObjectsTypes.CUSTOMER)
        .pipe(
          map((res) => (res.data ?? []).filter((def) => FILTERABLE_CUSTOM_FIELD_TYPES.has(def.field_type))),
          catchError(() => of([] as ICustomField[])),
        ),
      locationGroups: this.companiesGroupsService.listCompaniesGroups().pipe(
        map((res) => res.data ?? []),
        catchError(() => of([] as ICompaniesGroups[])),
      ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ customFieldDefinitions, locationGroups }) => {
        this.customFieldDefinitions = customFieldDefinitions;
        this.locationGroups = locationGroups;
        this.filterOptionsReady = true;
      });

    this.loadLocations();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cancelPendingFetch$.complete();
  }

  // ── Search step ───────────────────────────────────────────────────────────

  private loadLocations(): void {
    // Cancel a still-in-flight fetch from the previous search/filter/scroll
    // before starting this one -- see cancelPendingFetch$ above.
    this.cancelPendingFetch$.next();
    this.loading = true;
    this.isLoadingMore = false;
    this.pageNumber = 1;
    this.sortedLocations = [];
    this.canLoadMore = false;
    this.matchingCount = null;
    // A new search/filter invalidates any "select all" claim about the
    // PREVIOUS result set -- without this, the banner keeps reading "All N
    // selected" for a search it no longer describes, and canSelectAllMatching
    // (which requires !selectAllMatchingActive) hides the link for the new
    // one. This does not touch the selection itself -- ids already picked
    // (manually or via a prior select-all) are kept.
    this.selectAllMatchingActive = false;
    this.fetchLocationsPage(1, false);
  }

  /** Scroll handler for the results body: fetches the next page once the
   * admin has scrolled near the bottom, same threshold/shape as the other
   * infinite-scroll lists (see CompanyLocationSelectorComponent). */
  onResultsScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const ratio = (target.scrollTop + target.clientHeight) / target.scrollHeight;
    if (ratio > SCROLL_LOAD_MORE_THRESHOLD) {
      this.loadMoreLocations();
    }
  }

  private loadMoreLocations(): void {
    if (this.loading || this.isLoadingMore || !this.canLoadMore) return;
    this.isLoadingMore = true;
    this.fetchLocationsPage(this.pageNumber + 1, true);
  }

  private fetchLocationsPage(page: number, append: boolean): void {
    const filterPayload = this.buildFilterPayload();
    // This picker exists to add NEW exclusions -- a location already on the
    // list is a no-op if re-added, so it's dropped from the search/listing
    // entirely rather than shown as pickable clutter.
    this.customersService.listLocations({ number: page, size: PAGE_SIZE }, filterPayload, this.keyword || undefined, 'id', 'desc', true).pipe(
      // A new search/filter/scroll cancels whatever the PREVIOUS one was
      // still waiting on -- otherwise a stale response can land after the
      // list has already been reset and silently corrupt pageNumber/results.
      takeUntil(this.cancelPendingFetch$),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (res) => {
        const rows = (res.data as ILocation[]) ?? [];
        const newLocations = rows.filter(
          (location): location is ILocation & { id: number } => location.id != null,
        );
        this.pageNumber = page;
        this.sortedLocations = append ? [...this.sortedLocations, ...newLocations] : newLocations;
        this.matchingCount = res.metadata?.count ?? null;
        this.canLoadMore = rows.length === PAGE_SIZE;
        this.loading = false;
        this.isLoadingMore = false;
      },
      error: () => {
        if (!append) {
          this.sortedLocations = [];
        }
        this.canLoadMore = false;
        this.loading = false;
        this.isLoadingMore = false;
      },
    });
  }

  /** Translate `activeFilterValues` (has_billing + one entry per dynamic
   * custom field key) into the `{filters: [...]}` shape build_location_domain
   * expects. */
  private buildFilterPayload(): ICompanyFilterRequest | undefined {
    const filters: { key: string; operator: string; value: any }[] = [];

    const hasBilling = this.activeFilterValues['has_billing'];
    if (hasBilling !== undefined && hasBilling !== null && hasBilling !== '') {
      filters.push({ key: 'has_billing', operator: 'equals', value: hasBilling === 'true' || hasBilling === true });
    }

    const isMain = this.activeFilterValues['is_main'];
    if (isMain !== undefined && isMain !== null && isMain !== '') {
      filters.push({ key: 'is_main', operator: 'equals', value: isMain === 'true' || isMain === true });
    }

    const groupIds = this.activeFilterValues['groups'];
    if (Array.isArray(groupIds) && groupIds.length > 0) {
      filters.push({ key: 'groups', operator: 'in', value: groupIds });
    }

    for (const definition of this.customFieldDefinitions) {
      const key = customFieldKey(definition.id);
      const value = this.activeFilterValues[key];
      if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) continue;

      // A multi-select over real recorded values (Array.isArray(value)) is
      // always an exact membership match on the backend regardless of
      // operator -- 'in' just documents that at the call site. A still-free-
      // text search field (no recorded values yet) keeps 'contains'.
      const operator = Array.isArray(value)
        ? 'in'
        : definition.field_type === CustomFieldTypes.BOOLEAN
          ? 'equals'
          : definition.field_type === CustomFieldTypes.TEXT ||
              definition.field_type === CustomFieldTypes.URL ||
              definition.field_type === CustomFieldTypes.EMAIL
            ? 'contains'
            : 'equals';

      const resolvedValue = definition.field_type === CustomFieldTypes.BOOLEAN ? value === 'true' || value === true : value;

      filters.push({ key: 'custom_field', field_definition_id: definition.id, operator, value: resolvedValue } as any);
    }

    return filters.length > 0 ? { filters } : undefined;
  }

  onSearch(value: string): void {
    this.keyword = value;
    this.loadLocations();
  }

  // ── Selection ─────────────────────────────────────────────────────────────

  isSelected(id: number): boolean {
    return this.selectedById.has(id);
  }

  get selectedCount(): number {
    return this.selectedById.size;
  }

  get hasSelection(): boolean {
    return this.selectedById.size > 0;
  }

  /** Header checkbox state: every currently-rendered row is selected. An
   * empty list is false, same convention as SelectionController. */
  get isAllVisibleSelected(): boolean {
    return this.sortedLocations.length > 0 && this.sortedLocations.every((location) => this.isSelected(location.id));
  }

  get isPartialVisibleSelected(): boolean {
    return this.selectedCount > 0 && !this.isAllVisibleSelected;
  }

  toggleLocation(location: ILocation & { id: number }, checked: boolean): void {
    if (checked) {
      this.selectedById.set(location.id, { id: location.id, name: location.name ?? '', erp_id: location.erp_id ?? null });
    } else {
      this.selectedById.delete(location.id);
      this.selectAllMatchingActive = false;
    }
  }

  /** Header checkbox: selects/deselects only the rows currently rendered --
   * "Select all N matching" (below) is the bulk action for everything else. */
  toggleAllVisible(checked: boolean): void {
    if (!checked && this.selectAllMatchingActive) {
      // Unchecking the header while a "select all N matching" is active must
      // clear the WHOLE selection, not just the visible page -- otherwise
      // the off-screen rows stay selected while the header/banner both read
      // as fully cleared, silently including them in the next Confirm.
      this.clearSelection();
      return;
    }
    this.sortedLocations.forEach((location) => this.toggleLocation(location, checked));
  }

  removeSelected(id: number): void {
    this.selectedById.delete(id);
    this.selectAllMatchingActive = false;
  }

  /** Whether the "Select all N matching" action has anything left to offer. */
  get canSelectAllMatching(): boolean {
    return this.matchingCount != null && this.matchingCount > 0 && !this.selectAllMatchingActive;
  }

  /**
   * Fetches every id (+ name/erp_id) matching the current search/filter from
   * the dedicated, unpaginated select-all endpoint and adds them all to the
   * selection -- without requiring `sortedLocations` to ever hold more than
   * a screenful. Keeps the browsing list small and fast regardless of how
   * many locations actually match.
   */
  selectAllMatching(): void {
    if (this.isSelectingAll) return;
    this.isSelectingAll = true;
    const filterPayload = this.buildFilterPayload();
    // Same as the paginated fetch -- never bulk-select a location that's
    // already on the exclusion list.
    this.customersService.fetchAllLocationsForSelectAll(filterPayload, this.keyword || undefined, true).subscribe({
      next: (res) => {
        const rows = res.data ?? [];
        rows.forEach((row) => {
          if (row.id != null) this.selectedById.set(row.id, { id: row.id, name: row.name ?? '', erp_id: row.erp_id ?? null });
        });
        this.selectAllMatchingActive = true;
        this.isSelectingAll = false;
      },
      error: () => {
        this.isSelectingAll = false;
      },
    });
  }

  clearSelection(): void {
    this.selectedById.clear();
    this.selectAllMatchingActive = false;
  }

  // ── Filter dialog ─────────────────────────────────────────────────────────

  getCustomFieldLabel(def: ICustomField): string {
    return def?.label?.[this.currentLanguage] || Object.values(def?.label ?? {}).find((v) => v !== '') || def?.technical_name || '';
  }

  private buildCustomFieldFilterField(def: ICustomField): FilterField {
    const key = customFieldKey(def.id);
    const label = this.getCustomFieldLabel(def);

    if (def.field_type === CustomFieldTypes.BOOLEAN) {
      return {
        key,
        label,
        type: 'radio' as FilterFieldType,
        options: [
          { value: 'true', label: 'common.yes' },
          { value: 'false', label: 'common.no' },
        ],
      };
    }
    if (def.field_type === CustomFieldTypes.SINGLE_SELECT) {
      return {
        key,
        label,
        type: 'single-select' as FilterFieldType,
        options: (def.allowed_values ?? []).map((v) => ({ value: v, label: v })),
      };
    }
    if (def.field_type === CustomFieldTypes.MULTI_SELECT) {
      return {
        key,
        label,
        type: 'multi-select' as FilterFieldType,
        options: (def.allowed_values ?? []).map((v) => ({ value: v, label: v })),
      };
    }
    // text / url / email -- free-text contains-match search, same as the
    // location name/ERP search box.
    return {
      key,
      label,
      type: 'search' as FilterFieldType,
      placeholder: 'knowledge_base.shipping_exclusions.filter.custom_field_placeholder',
    };
  }

  private get filterConfig(): FilterDialogData {
    return {
      config: {
        title: 'knowledge_base.shipping_exclusions.filter.title',
        sections: [
          {
            title: 'knowledge_base.shipping_exclusions.filter.group_title',
            fields: [
              {
                key: 'has_billing',
                label: 'knowledge_base.shipping_exclusions.filter.has_billing',
                type: 'radio',
                options: [
                  { value: 'true', label: 'common.yes' },
                  { value: 'false', label: 'common.no' },
                ],
              },
              {
                key: 'is_main',
                label: 'knowledge_base.shipping_exclusions.filter.is_main',
                type: 'radio',
                options: [
                  { value: 'true', label: 'common.yes' },
                  { value: 'false', label: 'common.no' },
                ],
              },
              ...(this.locationGroups.length > 0
                ? [
                    {
                      key: 'groups',
                      label: 'knowledge_base.shipping_exclusions.filter.customer_groups',
                      // Renders as the platform's collapsed dropdown-select
                      // widget (ui-form-field, type=select+multiple), not a
                      // raw <select multiple> -- FilterDialogComponent's
                      // multi-select field delegates to it. A native
                      // <select multiple> with two-way ngModel binding hung
                      // the tab under real (non-synthetic) interaction.
                      type: 'multi-select' as FilterFieldType,
                      // A tenant can have many customer groups -- a plain
                      // unsearchable list doesn't scale, unlike Magazzino/
                      // Zona's short fixed option sets.
                      searchable: true,
                      options: this.locationGroups.map((group) => ({ value: String(group.id), label: group.name })),
                    },
                  ]
                : []),
            ],
          },
          ...(this.customFieldDefinitions.length > 0
            ? [
                {
                  title: 'knowledge_base.shipping_exclusions.filter.custom_fields_group_title',
                  fields: this.customFieldDefinitions.map((def) => this.buildCustomFieldFilterField(def)),
                },
              ]
            : []),
        ],
      },
      values: {},
    };
  }

  setFilter(): void {
    if (!this.filterOptionsReady) return;
    const dialogRef = this.dialog.open(FilterDialogComponent, {
      data: { ...this.filterConfig, values: { ...this.activeFilterValues } } as FilterDialogData,
      // Matches the width every other FilterDialogComponent caller uses
      // (e.g. Ignore Codes) -- a narrower width let the 2-column field
      // pairing + footer buttons overflow horizontally, cutting off Reset/Apply.
      width: '900px',
      // This dialog opens ON TOP OF an already-open MatDialog (this modal
      // itself), unlike every other FilterDialogComponent caller which opens
      // it directly from a page. CDK's default autoFocus tries to move focus
      // into this dialog while the OUTER dialog's own focus trap is still
      // live in the background (now marked aria-hidden) -- with a real
      // mouse-driven click (not a programmatic one) the two focus traps can
      // fight and hang the tab ("Blocked aria-hidden on an element because
      // its descendant retained focus"). Disabling autoFocus here avoids
      // that fight; restoreFocus: false likewise skips trying to return
      // focus to a trigger button that's inside another dialog.
      autoFocus: false,
      restoreFocus: false,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: Record<string, any> | undefined) => {
        if (result) {
          this.activeFilterValues = result;
          this.loadLocations();
        }
      });
  }

  hasActiveFilters(): boolean {
    return Object.values(this.activeFilterValues).some(
      (value) => value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0),
    );
  }

  activeFilterCount(): number {
    return Object.values(this.activeFilterValues).filter(
      (value) => value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0),
    ).length;
  }

  clearFilter(): void {
    this.activeFilterValues = {};
    this.loadLocations();
  }

  // ── Step navigation ───────────────────────────────────────────────────────

  goToPreview(): void {
    if (!this.hasSelection) return;
    this.step = 'preview';
  }

  goBackToSearch(): void {
    this.step = 'search';
  }

  get selectedLocations(): ILocationSelectAllRow[] {
    return [...this.selectedById.values()];
  }

  /** Preview-step list rows, capped at PREVIEW_ROWS_LIMIT -- see its doc
   * comment. Cheaper than `selectedLocations` for a huge selection since it
   * stops iterating the Map once the cap is hit instead of materializing
   * every entry just to slice it afterwards. */
  get previewLocations(): ILocationSelectAllRow[] {
    const rows: ILocationSelectAllRow[] = [];
    for (const row of this.selectedById.values()) {
      if (rows.length >= PREVIEW_ROWS_LIMIT) break;
      rows.push(row);
    }
    return rows;
  }

  get previewHiddenCount(): number {
    return Math.max(0, this.selectedCount - PREVIEW_ROWS_LIMIT);
  }

  // ── Confirm ───────────────────────────────────────────────────────────────

  submitPermission() {
    return { module: 'company', action: 'edit' };
  }

  confirm(): void {
    if (!this.canSubmit || !this.hasSelection) return;
    this.submitting$.pipe(take(1), takeUntil(this.destroy$)).subscribe((isSubmitting) => {
      if (isSubmitting) return;
      this.store.dispatch(BulkCreateLocationShippingExclusions({ locationIds: [...this.selectedById.keys()] }));
    });
  }

  // ── Close ─────────────────────────────────────────────────────────────────

  close(): void {
    this.dialogRef.close();
  }
}
